import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, Check } from "lucide-react";
import { useLocalizedNavigate } from "@/lib/useLocalizedNavigate";

const OPTIONS = [
  { code: "pt", flag: "🇧🇷", short: "PT-BR", label: "Português (BR)" },
  { code: "en", flag: "🇺🇸", short: "EN",    label: "English" },
];

export function LangSwitcher({ dark = false }) {
  const { i18n } = useTranslation();
  const { current, switchLanguage } = useLocalizedNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const active = OPTIONS.find((o) => o.code === current) || OPTIONS[0];
  const otherLang = i18n.language?.slice(0, 2) === "en"
    ? { switchTo: "Português", switchToNative: "Mudar para Português" }
    : { switchTo: "English", switchToNative: "Switch to English" };

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="lang-switcher"
        onClick={() => setOpen((o) => !o)}
        aria-label={otherLang.switchToNative}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
          dark
            ? "border-zinc-700 text-zinc-300 hover:border-zinc-500 bg-transparent"
            : "border-zinc-200 text-zinc-700 hover:border-zinc-400 bg-white"
        }`}
      >
        <span className="text-sm leading-none" aria-hidden>{active.flag}</span>
        <span>{active.short}</span>
        <ChevronDown size={12} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <ul
          data-testid="lang-switcher-menu"
          role="listbox"
          className={`absolute right-0 top-full mt-2 min-w-[180px] py-1.5 rounded-lg shadow-lg border z-50 ${
            dark ? "bg-zinc-900 border-zinc-800" : "bg-white border-zinc-200"
          }`}
        >
          {OPTIONS.map((opt) => (
            <li key={opt.code}>
              <button
                data-testid={`lang-option-${opt.code}`}
                onClick={() => { setOpen(false); switchLanguage(opt.code); }}
                role="option"
                aria-selected={opt.code === current}
                className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left transition-colors ${
                  dark
                    ? "text-zinc-200 hover:bg-zinc-800"
                    : "text-zinc-800 hover:bg-zinc-50"
                }`}
              >
                <span className="text-base leading-none" aria-hidden>{opt.flag}</span>
                <span className="flex-1 font-medium">{opt.label}</span>
                {opt.code === current && <Check size={14} className="text-[#FF4F00]" />}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
